import React, { useMemo, useState, Fragment } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageSquare, Smartphone, Globe, Mail, Phone, Brain, Database, Activity, Shield, Zap, Clock, CheckCircle2, XCircle } from 'lucide-react';
// ============================================================================
// MOCK DATA
// ============================================================================
interface ChannelData {
  id: string;
  name: string;
  icon: 'chat' | 'mobile' | 'api' | 'email' | 'whatsapp';
  color: string;
  requestsPerDay: number;
}
interface KnowledgeData {
  id: string;
  name: string;
  documents: number;
  status: 'Attivo' | 'In aggiornamento';
}
interface LogEntry {
  id: string;
  timestamp: string;
  user: string;
  action: string;
  status: 'success' | 'error';
  cost: number;
  latency: number;
}
interface ServiceDetailData {
  id: string;
  name: string;
  type: string;
  model: string;
  health: 'healthy' | 'warning' | 'critical';
  cost: number;
  compliance: number;
  requestsPerDay: number;
  tokens: string;
  description: string;
  channels: ChannelData[];
  knowledge: KnowledgeData[];
  logs: LogEntry[];
}
const SERVICE_DETAIL_DATA: Record<string, ServiceDetailData> = {
  s1: {
    id: 's1',
    name: 'Customer Assistant',
    type: 'Chatbot',
    model: 'GPT-4o',
    health: 'healthy',
    cost: 45,
    compliance: 92,
    requestsPerDay: 1250,
    tokens: '450K',
    description: 'Primary customer support agent handling L1 queries.',
    channels: [{
      id: 'ch1',
      name: 'Web Chat',
      icon: 'chat',
      color: '#3b82f6',
      requestsPerDay: 680
    }, {
      id: 'ch2',
      name: 'WhatsApp',
      icon: 'whatsapp',
      color: '#25d366',
      requestsPerDay: 320
    }, {
      id: 'ch3',
      name: 'Email',
      icon: 'email',
      color: '#f59e0b',
      requestsPerDay: 150
    }, {
      id: 'ch4',
      name: 'API',
      icon: 'api',
      color: '#8b5cf6',
      requestsPerDay: 100
    }],
    knowledge: [{
      id: 'kb1',
      name: 'FAQ Database',
      documents: 142,
      status: 'Attivo'
    }, {
      id: 'kb2',
      name: 'Product Catalog',
      documents: 89,
      status: 'Attivo'
    }, {
      id: 'kb3',
      name: 'Return Policy',
      documents: 34,
      status: 'Attivo'
    }],
    logs: [{
      id: 'l1',
      timestamp: '14:32:15',
      user: 'Marco B.',
      action: 'Richiesta info prodotto',
      status: 'success',
      cost: 0.08,
      latency: 1.2
    }, {
      id: 'l2',
      timestamp: '14:31:42',
      user: 'Sofia R.',
      action: 'Tracking ordine #4521',
      status: 'success',
      cost: 0.05,
      latency: 0.8
    }, {
      id: 'l3',
      timestamp: '14:30:18',
      user: 'Luca V.',
      action: 'Richiesta reso',
      status: 'success',
      cost: 0.12,
      latency: 1.5
    }, {
      id: 'l4',
      timestamp: '14:28:55',
      user: 'Anna N.',
      action: 'Domanda su garanzia',
      status: 'error',
      cost: 0.03,
      latency: 0.5
    }, {
      id: 'l5',
      timestamp: '14:27:30',
      user: 'Giovanni R.',
      action: 'Info spedizione',
      status: 'success',
      cost: 0.06,
      latency: 0.9
    }, {
      id: 'l6',
      timestamp: '14:25:12',
      user: 'Matteo L.',
      action: 'Problema pagamento',
      status: 'success',
      cost: 0.15,
      latency: 2.1
    }, {
      id: 'l7',
      timestamp: '14:22:45',
      user: 'Giulia P.',
      action: 'Info orari negozio',
      status: 'success',
      cost: 0.04,
      latency: 0.6
    }]
  },
  s2: {
    id: 's2',
    name: 'Smart Matcher',
    type: 'Matcher',
    model: 'GPT-4 Turbo',
    health: 'healthy',
    cost: 32,
    compliance: 88,
    requestsPerDay: 5500,
    tokens: '1.2M',
    description: 'Matches user intent to relevant knowledge base articles.',
    channels: [{
      id: 'ch1',
      name: 'API',
      icon: 'api',
      color: '#8b5cf6',
      requestsPerDay: 4200
    }, {
      id: 'ch2',
      name: 'Web Platform',
      icon: 'chat',
      color: '#3b82f6',
      requestsPerDay: 1100
    }, {
      id: 'ch3',
      name: 'Mobile App',
      icon: 'mobile',
      color: '#10b981',
      requestsPerDay: 200
    }],
    knowledge: [{
      id: 'kb1',
      name: 'Knowledge Base',
      documents: 1250,
      status: 'Attivo'
    }, {
      id: 'kb2',
      name: 'Product Documentation',
      documents: 340,
      status: 'Attivo'
    }],
    logs: [{
      id: 'l1',
      timestamp: '14:32:45',
      user: 'System',
      action: 'Match query → KB article #892',
      status: 'success',
      cost: 0.02,
      latency: 0.4
    }, {
      id: 'l2',
      timestamp: '14:32:44',
      user: 'System',
      action: 'Match query → KB article #156',
      status: 'success',
      cost: 0.02,
      latency: 0.3
    }, {
      id: 'l3',
      timestamp: '14:32:43',
      user: 'System',
      action: 'No match found',
      status: 'error',
      cost: 0.01,
      latency: 0.2
    }, {
      id: 'l4',
      timestamp: '14:32:42',
      user: 'System',
      action: 'Match query → KB article #445',
      status: 'success',
      cost: 0.02,
      latency: 0.4
    }, {
      id: 'l5',
      timestamp: '14:32:41',
      user: 'System',
      action: 'Match query → KB article #1102',
      status: 'success',
      cost: 0.02,
      latency: 0.3
    }]
  },
  s3: {
    id: 's3',
    name: 'Content Moderator',
    type: 'Moderator',
    model: 'Claude 3.5',
    health: 'warning',
    cost: 18,
    compliance: 71,
    requestsPerDay: 800,
    tokens: '200K',
    description: 'Filters and flags inappropriate content in real-time.',
    channels: [{
      id: 'ch1',
      name: 'API',
      icon: 'api',
      color: '#8b5cf6',
      requestsPerDay: 500
    }, {
      id: 'ch2',
      name: 'Web Platform',
      icon: 'chat',
      color: '#3b82f6',
      requestsPerDay: 200
    }, {
      id: 'ch3',
      name: 'Mobile App',
      icon: 'mobile',
      color: '#10b981',
      requestsPerDay: 100
    }],
    knowledge: [{
      id: 'kb1',
      name: 'Content Guidelines',
      documents: 56,
      status: 'Attivo'
    }, {
      id: 'kb2',
      name: 'Blacklist Terms',
      documents: 1200,
      status: 'In aggiornamento'
    }],
    logs: [{
      id: 'l1',
      timestamp: '14:33:02',
      user: 'System',
      action: 'Content flagged: spam',
      status: 'success',
      cost: 0.04,
      latency: 0.8
    }, {
      id: 'l2',
      timestamp: '14:32:58',
      user: 'System',
      action: 'Content approved',
      status: 'success',
      cost: 0.03,
      latency: 0.7
    }, {
      id: 'l3',
      timestamp: '14:32:51',
      user: 'System',
      action: 'Content flagged: inappropriate',
      status: 'success',
      cost: 0.04,
      latency: 0.9
    }, {
      id: 'l4',
      timestamp: '14:32:45',
      user: 'System',
      action: 'Analysis timeout',
      status: 'error',
      cost: 0.01,
      latency: 5.0
    }, {
      id: 'l5',
      timestamp: '14:32:38',
      user: 'System',
      action: 'Content approved',
      status: 'success',
      cost: 0.03,
      latency: 0.6
    }]
  },
  s4: {
    id: 's4',
    name: 'AI Tutor',
    type: 'Tutor',
    model: 'GPT-4 Turbo',
    health: 'healthy',
    cost: 28,
    compliance: 95,
    requestsPerDay: 560,
    tokens: '300K',
    description: 'Internal training assistant for employee onboarding.',
    channels: [{
      id: 'ch1',
      name: 'Web Platform',
      icon: 'chat',
      color: '#3b82f6',
      requestsPerDay: 380
    }, {
      id: 'ch2',
      name: 'Mobile App',
      icon: 'mobile',
      color: '#10b981',
      requestsPerDay: 150
    }, {
      id: 'ch3',
      name: 'Email',
      icon: 'email',
      color: '#f59e0b',
      requestsPerDay: 30
    }],
    knowledge: [{
      id: 'kb1',
      name: 'Training Materials',
      documents: 245,
      status: 'Attivo'
    }, {
      id: 'kb2',
      name: 'Company Policies',
      documents: 67,
      status: 'Attivo'
    }, {
      id: 'kb3',
      name: 'Onboarding Guide',
      documents: 23,
      status: 'Attivo'
    }],
    logs: [{
      id: 'l1',
      timestamp: '14:30:12',
      user: 'Elena M.',
      action: 'Quiz completato: 85%',
      status: 'success',
      cost: 0.15,
      latency: 2.5
    }, {
      id: 'l2',
      timestamp: '14:28:45',
      user: 'Roberto F.',
      action: 'Domanda su policy HR',
      status: 'success',
      cost: 0.08,
      latency: 1.8
    }, {
      id: 'l3',
      timestamp: '14:25:30',
      user: 'Chiara B.',
      action: 'Richiesta materiale corso',
      status: 'success',
      cost: 0.06,
      latency: 1.2
    }, {
      id: 'l4',
      timestamp: '14:22:18',
      user: 'Marco P.',
      action: 'Sessione tutoring',
      status: 'success',
      cost: 0.22,
      latency: 4.5
    }, {
      id: 'l5',
      timestamp: '14:18:55',
      user: 'Laura S.',
      action: 'Feedback negativo',
      status: 'error',
      cost: 0.04,
      latency: 0.8
    }]
  }
};
const getDefaultServiceData = (id: string): ServiceDetailData => ({
  id,
  name: 'Generic Service',
  type: 'Assistant',
  model: 'GPT-4',
  health: 'healthy',
  cost: 0,
  compliance: 100,
  requestsPerDay: 0,
  tokens: '0',
  description: 'A generic AI service.',
  channels: [],
  knowledge: [],
  logs: []
});
// ============================================================================
// COMPONENT
// ============================================================================
const ChannelIcon = ({
  icon,
  className,
  style
}: {
  icon: string;
  className?: string;
  style?: React.CSSProperties;
}) => {
  switch (icon) {
    case 'chat':
      return <MessageSquare className={className} style={style} data-id="element-3431" />;
    case 'mobile':
      return <Smartphone className={className} style={style} data-id="element-3432" />;
    case 'api':
      return <Globe className={className} style={style} data-id="element-3433" />;
    case 'email':
      return <Mail className={className} style={style} data-id="element-3434" />;
    case 'whatsapp':
      return <Phone className={className} style={style} data-id="element-3435" />;
    default:
      return <Globe className={className} style={style} data-id="element-3436" />;
  }
};
type TabType = 'overview' | 'canali' | 'knowledge' | 'log';
export function ServizioDetailPage() {
  const {
    agentId
  } = useParams<{
    agentId: string;
  }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const service = useMemo(() => {
    return SERVICE_DETAIL_DATA[agentId || ''] || getDefaultServiceData(agentId || 'unknown');
  }, [agentId]);
  const healthColor = service.health === 'healthy' ? 'text-[#8eff01]' : service.health === 'warning' ? 'text-amber-500' : 'text-red-500';
  const healthBg = service.health === 'healthy' ? 'bg-[#8eff01]' : service.health === 'warning' ? 'bg-amber-500' : 'bg-red-500';
  const tabs = [{
    id: 'overview',
    label: 'Overview'
  }, {
    id: 'canali',
    label: 'Canali'
  }, {
    id: 'knowledge',
    label: 'Knowledge'
  }, {
    id: 'log',
    label: 'Log'
  }];
  return <div className="min-h-screen bg-[#111114] pt-8 px-8 pb-12 space-y-8" data-id="element-3437">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-xs text-white/40" data-id="element-3438">
        <button onClick={() => navigate('/canvas')} className="hover:text-white transition-colors" data-id="element-3439">
          AI Map
        </button>
        <span data-id="element-3440">/</span>
        <span className="text-white/60" data-id="element-3441">{service.name}</span>
      </div>

      {/* Header */}
      <div className="flex flex-col gap-1" data-id="element-3442">
        <div className="flex items-center gap-3" data-id="element-3443">
          <h1 className="text-[24px] font-semibold text-white tracking-tight" data-id="element-3444">
            {service.name}
          </h1>
          <div className="flex items-center gap-2 px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/[0.06]" data-id="element-3445">
            <div className={`w-1.5 h-1.5 rounded-full ${healthBg}`} data-id="element-3446" />
            <span className="text-[11px] font-medium text-white/60 capitalize" data-id="element-3447">
              {service.health}
            </span>
          </div>
          <span className="px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/[0.06] text-[11px] font-medium text-white/60" data-id="element-3448">
            {service.type}
          </span>
          <span className="px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/[0.06] text-[11px] font-medium text-white/60" data-id="element-3449">
            {service.model}
          </span>
        </div>
        <p className="text-[14px] text-white/40 max-w-2xl" data-id="element-3450">
          {service.description}
        </p>
      </div>

      {/* Metrics Row */}
      <div className="flex items-center gap-8 py-2" data-id="element-3451">
        <div data-id="element-3452">
          <p className="text-[11px] text-white/35 uppercase tracking-wider mb-1" data-id="element-3453">
            Costo Mensile
          </p>
          <p className="text-[18px] font-semibold text-white" data-id="element-3454">
            €{service.cost}/mo
          </p>
        </div>
        <div className="w-px h-8 bg-white/[0.08]" data-id="element-3455" />
        <div data-id="element-3456">
          <p className="text-[11px] text-white/35 uppercase tracking-wider mb-1" data-id="element-3457">
            Richieste/Giorno
          </p>
          <p className="text-[18px] font-semibold text-white" data-id="element-3458">
            {service.requestsPerDay.toLocaleString()}
          </p>
        </div>
        <div className="w-px h-8 bg-white/[0.08]" data-id="element-3459" />
        <div data-id="element-3460">
          <p className="text-[11px] text-white/35 uppercase tracking-wider mb-1" data-id="element-3461">
            Compliance
          </p>
          <p className={`text-[18px] font-semibold ${service.compliance >= 90 ? 'text-[#8eff01]' : 'text-amber-500'}`} data-id="element-3462">
            {service.compliance}%
          </p>
        </div>
        <div className="w-px h-8 bg-white/[0.08]" data-id="element-3463" />
        <div data-id="element-3464">
          <p className="text-[11px] text-white/35 uppercase tracking-wider mb-1" data-id="element-3465">
            Token Usage
          </p>
          <p className="text-[18px] font-semibold text-white" data-id="element-3466">
            {service.tokens}
          </p>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="border-b border-white/[0.08]" data-id="element-3467">
        <div className="flex gap-1 overflow-x-auto pb-1" data-id="element-3468">
          {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id as TabType)} className={`
                flex items-center px-4 py-2.5 text-[13px] font-medium transition-all rounded-lg whitespace-nowrap relative
                ${activeTab === tab.id ? 'text-white bg-white/[0.06]' : 'text-white/40 hover:text-white/70 hover:bg-white/[0.02]'}
              `} data-id="element-3469">
              {tab.label}
            </button>)}
        </div>
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]" data-id="element-3470">
        {activeTab === 'overview' && <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-3471">
            <div className="space-y-6" data-id="element-3472">
              <div data-id="element-3473">
                <h3 className="text-[14px] font-medium text-white mb-4" data-id="element-3474">
                  Dettagli Servizio
                </h3>
                <div className="space-y-4" data-id="element-3475">
                  <div className="flex justify-between py-3 border-b border-white/[0.06]" data-id="element-3476">
                    <span className="text-[13px] text-white/45" data-id="element-3477">
                      ID Servizio
                    </span>
                    <span className="text-[13px] text-white/75 font-mono" data-id="element-3478">
                      {service.id}
                    </span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-white/[0.06]" data-id="element-3479">
                    <span className="text-[13px] text-white/45" data-id="element-3480">
                      Modello AI
                    </span>
                    <span className="text-[13px] text-white/75" data-id="element-3481">
                      {service.model}
                    </span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-white/[0.06]" data-id="element-3482">
                    <span className="text-[13px] text-white/45" data-id="element-3483">
                      Tipo Agente
                    </span>
                    <span className="text-[13px] text-white/75" data-id="element-3484">
                      {service.type}
                    </span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-white/[0.06]" data-id="element-3485">
                    <span className="text-[13px] text-white/45" data-id="element-3486">
                      Ultimo Aggiornamento
                    </span>
                    <span className="text-[13px] text-white/75" data-id="element-3487">2 ore fa</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6" data-id="element-3488">
              <div data-id="element-3489">
                <h3 className="text-[14px] font-medium text-white mb-4" data-id="element-3490">
                  Performance
                </h3>
                <div className="space-y-4" data-id="element-3491">
                  <div className="flex justify-between py-3 border-b border-white/[0.06]" data-id="element-3492">
                    <span className="text-[13px] text-white/45" data-id="element-3493">
                      Uptime (30gg)
                    </span>
                    <span className="text-[13px] text-[#8eff01]" data-id="element-3494">99.98%</span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-white/[0.06]" data-id="element-3495">
                    <span className="text-[13px] text-white/45" data-id="element-3496">
                      Latenza Media
                    </span>
                    <span className="text-[13px] text-white/75" data-id="element-3497">0.8s</span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-white/[0.06]" data-id="element-3498">
                    <span className="text-[13px] text-white/45" data-id="element-3499">
                      Tasso di Errore
                    </span>
                    <span className="text-[13px] text-white/75" data-id="element-3500">0.05%</span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-white/[0.06]" data-id="element-3501">
                    <span className="text-[13px] text-white/45" data-id="element-3502">
                      Feedback Positivi
                    </span>
                    <span className="text-[13px] text-white/75" data-id="element-3503">94%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>}

        {activeTab === 'canali' && <div className="max-w-3xl" data-id="element-3504">
            <div className="space-y-0" data-id="element-3505">
              {service.channels.map(channel => <div key={channel.id} className="flex items-center justify-between py-3 border-b border-white/[0.06]" data-id="element-3506">
                  <div className="flex items-center gap-3" data-id="element-3507">
                    <div className="w-1.5 h-1.5 rounded-full" style={{
                backgroundColor: channel.color
              }} data-id="element-3508" />
                    <ChannelIcon icon={channel.icon} className="w-4 h-4 text-white/40" data-id="element-3509" />
                    <span className="text-[13px] text-white/70" data-id="element-3510">
                      {channel.name}
                    </span>
                  </div>
                  <span className="text-[13px] text-white/40 font-mono" data-id="element-3511">
                    {channel.requestsPerDay.toLocaleString()} req/day
                  </span>
                </div>)}
            </div>
          </div>}

        {activeTab === 'knowledge' && <div className="max-w-3xl" data-id="element-3512">
            <div className="space-y-0" data-id="element-3513">
              {service.knowledge.map(kb => <div key={kb.id} className="flex items-center justify-between py-3 border-b border-white/[0.06]" data-id="element-3514">
                  <div className="flex items-center gap-3" data-id="element-3515">
                    <Brain className="w-4 h-4 text-purple-400/70" data-id="element-3516" />
                    <span className="text-[13px] text-white/70" data-id="element-3517">{kb.name}</span>
                  </div>
                  <div className="flex items-center gap-6" data-id="element-3518">
                    <span className="text-[13px] text-white/40 flex items-center gap-1.5" data-id="element-3519">
                      <Database className="w-3 h-3" data-id="element-3520" />
                      {kb.documents} doc
                    </span>
                    <div className="flex items-center gap-1.5" data-id="element-3521">
                      <div className={`w-1.5 h-1.5 rounded-full ${kb.status === 'Attivo' ? 'bg-[#8eff01]' : 'bg-amber-500'}`} data-id="element-3522" />
                      <span className={`text-[12px] ${kb.status === 'Attivo' ? 'text-[#8eff01]' : 'text-amber-500'}`} data-id="element-3523">
                        {kb.status}
                      </span>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>}

        {activeTab === 'log' && <div data-id="element-3524">
            <table className="w-full text-left border-collapse" data-id="element-3525">
              <thead data-id="element-3526">
                <tr className="border-b border-white/[0.10]" data-id="element-3527">
                  <th className="py-3 text-[11px] font-medium text-white/35 uppercase tracking-wider" data-id="element-3528">
                    Ora
                  </th>
                  <th className="py-3 text-[11px] font-medium text-white/35 uppercase tracking-wider" data-id="element-3529">
                    Utente
                  </th>
                  <th className="py-3 text-[11px] font-medium text-white/35 uppercase tracking-wider" data-id="element-3530">
                    Azione
                  </th>
                  <th className="py-3 text-[11px] font-medium text-white/35 uppercase tracking-wider" data-id="element-3531">
                    Stato
                  </th>
                  <th className="py-3 text-[11px] font-medium text-white/35 uppercase tracking-wider text-right" data-id="element-3532">
                    Costo
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.06]" data-id="element-3533">
                {service.logs.map(log => <tr key={log.id} className="hover:bg-white/[0.02] transition-colors" data-id="element-3534">
                    <td className="py-2.5 text-[13px] text-white/40 font-mono" data-id="element-3535">
                      {log.timestamp}
                    </td>
                    <td className="py-2.5 text-[13px] text-white/70" data-id="element-3536">
                      {log.user}
                    </td>
                    <td className="py-2.5 text-[13px] text-white/60" data-id="element-3537">
                      {log.action}
                    </td>
                    <td className="py-2.5" data-id="element-3538">
                      <span className={`text-[12px] font-medium ${log.status === 'success' ? 'text-[#8eff01]' : 'text-red-500'}`} data-id="element-3539">
                        {log.status === 'success' ? 'OK' : 'Errore'}
                      </span>
                    </td>
                    <td className="py-2.5 text-[13px] text-white/40 text-right font-mono" data-id="element-3540">
                      €{log.cost.toFixed(2)}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>
    </div>;
}